import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-md hover:bg-destructive/90 hover:shadow-lg",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:border-accent",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: 
          "hover:bg-accent/10 hover:text-accent-foreground",
        link: 
          "text-primary underline-offset-4 hover:underline",
        // Custom Hakxcore variants
        brand:
          "bg-brand text-brand-foreground shadow-md hover:shadow-glow-brand hover:bg-brand/90 active:scale-[0.98]",
        "brand-outline":
          "border-2 border-brand text-brand bg-transparent hover:bg-brand hover:text-brand-foreground transition-colors",
        accent:
          "bg-accent text-accent-foreground shadow-md hover:shadow-glow hover:bg-accent/90 active:scale-[0.98]",
        "accent-outline":
          "border-2 border-accent text-accent bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors",
        // Dark section variants
        "dark-primary":
          "bg-brand text-brand-foreground shadow-md hover:shadow-glow-brand hover:brightness-110 active:scale-[0.98]",
        "dark-secondary":
          "bg-dark-elevated text-dark-text border border-dark-border hover:bg-dark-border hover:border-accent/50 transition-colors",
        "dark-ghost":
          "text-dark-text hover:text-accent hover:bg-dark-elevated/50",
        "dark-outline":
          "border border-dark-border text-dark-text hover:border-accent hover:text-accent bg-transparent",
        // Glass variants
        glass:
          "bg-white/10 backdrop-blur-md text-dark-text border border-white/20 hover:bg-white/20 hover:border-white/30",
        "glass-accent":
          "bg-accent/20 backdrop-blur-md text-dark-text border border-accent/30 hover:bg-accent/30 hover:border-accent/50",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
